/**
 * SEO Config — meta keywords (English) + route meta via i18n (en/ar/zh/ko from language picker).
 */

import { applyBrandToText, getBrand } from './brand.config';
import {
  GLOBAL_KEYWORDS,
  getDefaultMetaDescription,
  keywordsFor,
} from './seo-keywords';
import i18n from '../i18n';
import {
  getDefaultDescription,
  getSeoCopy,
  SEO_ROUTE_I18N_KEYS,
} from './seo-i18n';

export function getSiteName(): string {
  return getBrand().fullName;
}

export function getSiteUrl(): string {
  return getBrand().siteUrl;
}

export function getDefaultDesc(lang?: string): string {
  const fromI18n = getDefaultDescription(lang);
  if (fromI18n) return fromI18n;
  return applyBrandToText(getDefaultMetaDescription());
}

const SITE_NAME = getSiteName();
const SITE_URL = getSiteUrl();
const DEFAULT_DESC = getDefaultMetaDescription();
const DEFAULT_KEYWORDS = GLOBAL_KEYWORDS;

export interface SeoMeta {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
}

/** Build full title with site name (50-60 chars optimal for Google) */
export function buildTitle(title: string, suffix = true): string {
  const site = getSiteName();
  const t = applyBrandToText(title);
  if (!t) return `${site} - Crypto Payment Gateway`;
  return suffix ? `${t} | ${site}` : t;
}

/** Get canonical URL for a path (never defaults to homepage for inner routes). */
export function getCanonical(path: string, search?: string): string {
  let pathOnly = (path.split('?')[0].split('#')[0] || '/').trim();
  if (!pathOnly.startsWith('/')) pathOnly = `/${pathOnly}`;
  pathOnly = pathOnly.replace(/\/+$/, '') || '/';

  const base =
    pathOnly === '/'
      ? `${getSiteUrl()}/`
      : `${getSiteUrl()}${pathOnly}`;

  const qs = search?.replace(/^\?/, '') ?? '';
  if (qs && (pathOnly === '/blog' || pathOnly.startsWith('/blog/category/') || pathOnly.startsWith('/blog/tag/'))) {
    const params = new URLSearchParams(qs);
    const pageNum = parseInt(params.get('page') || '1', 10);
    if (pageNum > 1) {
      return `${base}?page=${pageNum}`;
    }
  }

  return base;
}

function applyRouteI18n(meta: SeoMeta, path: string, lang?: string): SeoMeta {
  const routeKey = SEO_ROUTE_I18N_KEYS[path];
  if (!routeKey) return meta;
  const copy = getSeoCopy(routeKey, lang);
  if (!copy) return meta;
  return {
    ...meta,
    title: copy.title || meta.title,
    description: copy.description || meta.description,
  };
}

function localizeSeoMeta(meta: SeoMeta, path: string, lang?: string): SeoMeta {
  const withI18n = applyRouteI18n(meta, path, lang);
  return {
    ...withI18n,
    title: applyBrandToText(withI18n.title),
    description: withI18n.description ? applyBrandToText(withI18n.description) : undefined,
    keywords: withI18n.keywords,
  };
}

/** Route → SEO config. English, Arabic, Korean, Chinese. No Farsi. */
export const SEO_ROUTES: Record<string, SeoMeta> = {
  // ─── Public
  '/': {
    title: 'Crypto Payment Gateway | USDT Checkout',
    description:
      'Accept crypto payments with tropayment.com — merchant cryptocurrency gateway for USDT, USDC, and native coins on TRON, Ethereum, BSC, Polygon, and Solana. REST API, webhooks, hosted checkout.',
    keywords: keywordsFor('brand', 'legacy', 'cryptopay', 'gateway', 'stablecoin', 'coins', 'networks', 'api', 'ecommerce'),
  },
  '/features': {
    title: 'Crypto Payment Gateway Features',
    description:
      'Multi-chain crypto payment gateway: fast USDT detection, HD wallets, HMAC merchant API, webhooks, QR checkout, KYC. TRON, Ethereum, BSC, Polygon, Solana.',
    keywords: keywordsFor('brand', 'gateway', 'stablecoin', 'networks', 'api'),
  },
  '/pricing': {
    title: 'Crypto Payment Gateway Pricing & Fees',
    description:
      'Transparent crypto payment gateway pricing. USDT payment fees, merchant plans, free tier for stablecoins and native coins on TRON, Ethereum, BSC, Polygon, Solana.',
    keywords: keywordsFor('brand', 'gateway', 'stablecoin', 'cryptopay'),
  },
  '/about': {
    title: 'About tropayment.com — Trusted Crypto Payment Gateway Since 2016',
    description:
      'About tropayment.com — cryptocurrency payment gateway for global merchants since 2016. Accept USDT, USDC, TRX, ETH, BNB, MATIC, SOL on TRON, Ethereum, BSC, Polygon, Solana. REST API, KYC, webhooks.',
    keywords: keywordsFor('brand', 'legacy', 'gateway', 'stablecoin', 'coins', 'networks', 'cryptopay'),
  },
  '/contact': {
    title: 'Contact Payment Gateway Support',
    description:
      'Contact tropayment.com for crypto payment gateway support, API integration, and merchant onboarding.',
    keywords: keywordsFor('brand', 'legacy', 'api', 'gateway'),
  },
  '/documentation': {
    title: 'Crypto Payment API Documentation',
    description:
      'Integrate cryptocurrency payments via REST API. Hosted checkout /pay, store POS /pos, QR scan pay, webhooks, HMAC auth, and USDT/TRC20/ERC20 examples.',
    keywords: keywordsFor('brand', 'api', 'gateway', 'stablecoin', 'networks', 'cryptopay'),
  },
  '/guide': {
    title: 'Platform User Guide — Features & How It Works',
    description:
      'Official {{brandFull}} user guide: POS terminal, QR checkout, camera scan pay, crypto wallets, invoices, virtual cards, KYC, Merchant API, and merchant dashboard.',
    keywords: keywordsFor('brand', 'gateway', 'api', 'stablecoin', 'cryptopay'),
  },
  '/apps': {
    title: 'Crypto Payment Apps & Extensions',
    description:
      'Download tropayment apps and browser extensions. Accept crypto payments on WooCommerce, mobile, and desktop — USDT gateway tools for merchants.',
    keywords: keywordsFor('brand', 'ecommerce', 'gateway', 'stablecoin'),
  },
  '/blog': {
    title: 'Crypto Payment Blog & Guides',
    description:
      'Expert articles on cryptocurrency payments, USDT checkout, merchant API integration, and crypto gateway guides from tropayment.com.',
    keywords: keywordsFor('brand', 'gateway', 'cryptopay', 'api', 'stablecoin'),
  },
  '/blog/tag': {
    title: 'Blog Topics & Tags',
    description:
      'Browse crypto payment articles by topic — USDT checkout, merchant API, and gateway integration guides.',
    keywords: keywordsFor('brand', 'gateway', 'cryptopay'),
  },
  '/verify-email': {
    title: 'Verify Email',
    description: 'Verify your Tropayment.com account email address.',
    keywords: keywordsFor('brand'),
    noIndex: true,
  },
  '/2fa': {
    title: 'Two-Factor Authentication',
    description: 'Complete two-factor authentication for your Tropayment.com account.',
    keywords: keywordsFor('brand'),
    noIndex: true,
  },
  '/login': {
    title: 'Login',
    description: 'Sign in to your Tropayment.com account.',
    keywords: keywordsFor('brand', 'gateway', 'cryptopay'),
    noIndex: true,
  },
  '/register': {
    title: 'Register Merchant Account',
    description: 'Create a tropayment.com merchant account — start accepting USDT and crypto payments via our payment gateway API.',
    keywords: keywordsFor('brand', 'gateway', 'cryptopay'),
    noIndex: true,
  },
  '/forgot-password': {
    title: 'Forgot Password',
    description: 'Reset your Tropayment.com account password.',
    keywords: keywordsFor('brand'),
    noIndex: true,
  },
  '/reset-password': {
    title: 'Reset Password',
    description: 'Set a new password for your Tropayment.com account.',
    keywords: keywordsFor('brand'),
    noIndex: true,
  },
  // ─── User Dashboard
  '/dashboard': {
    title: 'Dashboard',
    description: 'Your Tropayment.com dashboard. View balance, recent transactions, and quick actions.',
    keywords: keywordsFor('brand', 'gateway', 'stablecoin'),
    noIndex: true,
  },
  '/dashboard/kyc': {
    title: 'KYC Verification',
    description: 'Complete KYC verification for your Tropayment.com account.',
    noIndex: true,
  },
  '/dashboard/api-keys': {
    title: 'API Keys',
    description: 'Manage your Tropayment.com API keys for payment integration.',
    noIndex: true,
  },
  '/dashboard/wallet': {
    title: 'Wallet',
    description: 'Manage your Tropayment.com wallet and addresses.',
    noIndex: true,
  },
  '/dashboard/cards': {
    title: 'Virtual Cards',
    description: 'Issue and fund virtual cards from your tropayment.com wallet.',
    noIndex: true,
  },
  '/dashboard/invoices': {
    title: 'Invoices',
    description: 'Create and manage crypto payment invoices.',
    noIndex: true,
  },
  '/dashboard/transactions': {
    title: 'Transactions',
    description: 'View your payment transaction history.',
    noIndex: true,
  },
  '/dashboard/request-payment': {
    title: 'Request Payment',
    description: 'Create payment links to send to others.',
    noIndex: true,
  },
  '/dashboard/withdrawals': {
    title: 'Withdrawals',
    description: 'Manage your withdrawal requests and payouts.',
    noIndex: true,
  },
  '/dashboard/tickets': {
    title: 'Support Tickets',
    description: 'Your support tickets and messages.',
    noIndex: true,
  },
  '/dashboard/settings': {
    title: 'Settings',
    description: 'Account settings and preferences.',
    noIndex: true,
  },
  '/dashboard/activity': {
    title: 'Activity Log',
    description: 'Your account activity and login history.',
    noIndex: true,
  },
  // ─── Admin
  '/admin/adminLogin': {
    title: 'Admin Login',
    description: 'Tropayment.com admin panel login.',
    noIndex: true,
  },
  '/admin': {
    title: 'Admin Dashboard',
    description: 'Tropayment.com admin dashboard.',
    noIndex: true,
  },
  '/admin/wallets': {
    title: 'Wallet Management',
    description: 'Manage network wallets and connections.',
    keywords: keywordsFor('brand', 'networks'),
    noIndex: true,
  },
  '/admin/wallet-control': {
    title: 'Wallet Control',
    description: 'Platform financial summary — custody, treasury, and merchant liability.',
    keywords: keywordsFor('brand', 'networks'),
    noIndex: true,
  },
  '/admin/wallet-infrastructure': {
    title: 'Wallet Infrastructure',
    description: 'Internal wallet addresses, HD derivation, and gas configuration.',
    keywords: keywordsFor('brand', 'networks'),
    noIndex: true,
  },
  '/admin/wallet-audit': {
    title: 'Wallet Audit',
    description: 'Reconciliation and sweep audit — no addresses.',
    keywords: keywordsFor('brand', 'networks'),
    noIndex: true,
  },
  '/admin/hd-wallet': {
    title: 'HD Wallet',
    description: 'Unified BIP39 HD wallet status and diagnostics.',
    keywords: keywordsFor('brand', 'networks'),
    noIndex: true,
  },
  '/admin/apps': {
    title: 'Apps',
    description: 'Manage merchant apps and integrations.',
    keywords: keywordsFor('brand', 'ecommerce'),
    noIndex: true,
  },
  '/admin/users': {
    title: 'Users',
    description: 'Manage Tropayment.com users.',
    noIndex: true,
  },
  '/admin/kyc': {
    title: 'KYC Management',
    description: 'Review and manage KYC verifications.',
    noIndex: true,
  },
  '/admin/api-keys': {
    title: 'API Keys',
    description: 'Manage user API keys.',
    noIndex: true,
  },
  '/admin/invoices': {
    title: 'Invoices',
    description: 'View and manage invoices.',
    noIndex: true,
  },
  '/admin/transactions': {
    title: 'Transactions',
    description: 'View all transactions.',
    noIndex: true,
  },
  '/admin/withdrawals': {
    title: 'Withdrawals',
    description: 'Process and manage withdrawals.',
    noIndex: true,
  },
  '/admin/tickets': {
    title: 'Tickets',
    description: 'Support ticket management.',
    noIndex: true,
  },
  '/admin/suspicious': {
    title: 'Suspicious Transactions',
    description: 'Review flagged transactions.',
    noIndex: true,
  },
  '/admin/network': {
    title: 'Network Status',
    description: 'Blockchain network health and configuration.',
    noIndex: true,
  },
  '/admin/plans': {
    title: 'Plans',
    description: 'Manage subscription plans.',
    noIndex: true,
  },
  '/admin/team': {
    title: 'Admin Team',
    description: 'Manage admin users and permissions.',
    noIndex: true,
  },
  '/admin/settings': {
    title: 'Settings',
    description: 'Platform settings.',
    noIndex: true,
  },
  '/admin/pages': {
    title: 'Pages (CMS)',
    description: 'Manage site pages and content. Add, edit pages with SEO meta and rich content.',
    noIndex: true,
  },
  '/admin/blog': {
    title: 'Blog & Articles',
    description: 'Manage SEO blog articles, categories, tags, and featured images.',
    noIndex: true,
  },
  '/admin/menu': {
    title: 'Menu',
    description: 'Manage header, footer and sidebar menu items.',
    noIndex: true,
  },
  '/admin/logs': {
    title: 'Logs',
    description: 'System and error logs.',
    noIndex: true,
  },
  '/admin/live-chat': {
    title: 'Live Chat',
    description: 'Live chat with users.',
    noIndex: true,
  },
  '/admin/reports': {
    title: 'Financial Reports',
    description: 'Financial reports and analytics.',
    noIndex: true,
  },
  '/pay': {
    title: 'Payment',
    description: 'Complete your cryptocurrency payment.',
    keywords: keywordsFor('brand', 'stablecoin', 'coins', 'gateway'),
    noIndex: true,
  },
};

const PRIVATE_KEYWORDS = keywordsFor('brand', 'gateway');

function titleFromPath(path: string): string {
  const segment = path.split('/').filter(Boolean).pop() ?? 'page';
  return segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPrivatePath(path: string): boolean {
  return (
    path.startsWith('/dashboard') ||
    path.startsWith('/admin') ||
    path === '/login' ||
    path === '/register' ||
    path.startsWith('/pay/') ||
    path === '/pos' ||
    path === '/verify-email' ||
    path === '/2fa' ||
    path === '/forgot-password' ||
    path === '/reset-password'
  );
}

/** Get SEO config for path. Handles /admin/users/123 → /admin/users; always returns meta + keywords */
export function getSeoForPath(pathname: string, lang?: string): SeoMeta {
  const path = pathname.replace(/\/$/, '') || '/';
  let meta: SeoMeta | undefined = SEO_ROUTES[path];

  if (!meta && /^\/page\/[^/]+$/.test(path)) {
    const slug = path.replace('/page/', '');
    meta = {
      title: slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      description: getDefaultDesc(lang),
      keywords: GLOBAL_KEYWORDS,
    };
  }

  if (!meta && /^\/blog\/[^/]+$/.test(path) && !path.startsWith('/blog/category/') && !path.startsWith('/blog/tag/')) {
    const slug = path.replace('/blog/', '');
    meta = {
      title: slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      description: getDefaultDesc(lang),
      keywords: GLOBAL_KEYWORDS,
    };
  }

  if (!meta && /^\/blog\/category\/[^/]+$/.test(path)) {
    const slug = path.replace('/blog/category/', '');
    meta = {
      title: `${slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} — Blog`,
      description: getDefaultDesc(lang),
      keywords: GLOBAL_KEYWORDS,
    };
  }

  if (!meta && /^\/blog\/tag\/[^/]+$/.test(path)) {
    const slug = path.replace('/blog/tag/', '');
    const topicsSuffix = lang ? i18n.getFixedT(lang)('seo.topicsSuffix', { defaultValue: 'Topics' }) : 'Topics';
    meta = {
      title: `${slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} — ${topicsSuffix}`,
      description: getDefaultDesc(lang),
      keywords: keywordsFor('brand', 'gateway', 'cryptopay'),
    };
  }

  if (!meta && /^\/pay\/[^/]+$/.test(path)) {
    meta = {
      title: 'Pay Invoice',
      description: 'Complete your cryptocurrency payment securely.',
      keywords: keywordsFor('brand', 'stablecoin', 'coins', 'gateway'),
      noIndex: true,
    };
  }

  if (!meta) {
    const segments = path.split('/').filter(Boolean);
    for (let i = segments.length; i >= 1; i--) {
      const p = '/' + segments.slice(0, i).join('/');
      if (SEO_ROUTES[p]) {
        meta = SEO_ROUTES[p];
        break;
      }
    }
  }

  if (!meta) {
    meta = {
      title: titleFromPath(path),
      description: getDefaultDesc(lang),
      keywords: GLOBAL_KEYWORDS,
      noIndex: isPrivatePath(path),
    };
  }

  if (!meta.keywords) {
    meta = { ...meta, keywords: meta.noIndex ? PRIVATE_KEYWORDS : GLOBAL_KEYWORDS };
  }

  const localized = localizeSeoMeta(meta, path, lang);

  return {
    ...localized,
    canonical: getCanonical(path),
  };
}

export { GLOBAL_KEYWORDS, keywordsFor } from './seo-keywords';
export { getAlternateBrandNames } from './seo-keywords';

export { SITE_NAME, SITE_URL, DEFAULT_DESC, DEFAULT_KEYWORDS };
